import './button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'icon';

export interface ButtonOptions {
  variant?: ButtonVariant;
  label?: string;
  icon?: string | HTMLElement;
  onClick: (e: MouseEvent) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface ButtonHandle {
  readonly el: HTMLButtonElement;
  setLabel(text: string): void;
  setDisabled(disabled: boolean): void;
  destroy(): void;
}

export function createButton(opts: ButtonOptions): ButtonHandle {
  const variant: ButtonVariant = opts.variant ?? 'primary';
  if (!opts.label && !opts.icon) {
    throw new Error('[ui-kit] createButton: at least one of label or icon is required');
  }

  const isIconOnly = (variant === 'icon' && !opts.label) || (opts.icon && !opts.label);

  if (isIconOnly && !opts.ariaLabel) {
    throw new Error('[ui-kit] createButton: ariaLabel is required for icon-only buttons');
  }

  const el = document.createElement('button');
  el.type = 'button';
  el.className = `fg-btn fg-btn--${variant}`;
  if (opts.ariaLabel) el.setAttribute('aria-label', opts.ariaLabel);
  if (opts.disabled) el.setAttribute('disabled', '');

  const labelSpan = document.createElement('span');
  labelSpan.className = 'fg-btn__label';

  if (opts.icon) {
    const iconWrap = document.createElement('span');
    iconWrap.className = 'fg-btn__icon';
    if (typeof opts.icon === 'string') {
      iconWrap.textContent = opts.icon;
    } else {
      iconWrap.appendChild(opts.icon);
    }
    el.appendChild(iconWrap);
  }
  if (opts.label) {
    labelSpan.textContent = opts.label;
    el.appendChild(labelSpan);
  }

  const handler = (e: MouseEvent) => {
    if (el.hasAttribute('disabled')) return;
    opts.onClick(e);
  };
  el.addEventListener('click', handler);

  return {
    el,
    setLabel(text: string) {
      labelSpan.textContent = text;
      if (!labelSpan.parentNode) el.appendChild(labelSpan);
    },
    setDisabled(disabled: boolean) {
      if (disabled) el.setAttribute('disabled', '');
      else el.removeAttribute('disabled');
    },
    destroy() {
      el.removeEventListener('click', handler);
      el.remove();
    },
  };
}
